import { z } from 'zod'
import { ChangeNote, VersionReview, VersionState } from './defaultField.js'
import { DraftDefinitie } from './draft.js'
import { Block, DefinitieId, TemplateId } from './id.js'
import { Moment, Person } from './moment.js'
import { Text, TextDraft } from './text.js'

/**
 * Een sjabloon in de bibliotheek van formulierconfiguraties (D-34, besluit van 1 november 2026).
 *
 * **Een bevroren momentopname met versies.** Een sjabloon is een eigen object naast de definities: een
 * naam, het bouwblok waarvoor het is gemaakt, en een reeks onveranderlijke versies met elk een volledige
 * conceptstand en een wijzigingsnotitie. Een formulier uit een sjabloon is een kopie en onthoudt alleen
 * waar het vandaan kwam (`TemplateOrigin`); een nieuwe versie van het sjabloon verandert niets aan
 * formulieren die er eerder uit zijn gemaakt (D-36).
 *
 * **Geen werkblad op een sjabloon.** Een nieuwe versie maak je vanuit een formulier: je past het formulier
 * aan en slaat het op als nieuwe versie. Dan blijft de samenwerklaag, het automatisch opslaan (D-74) en
 * elke controle op één plek, de definitie.
 *
 * **Vier ogen**, zoals bij een standaardveld: een versie is een voorstel tot een tweede persoon met
 * Bibliotheekbeheer haar goedkeurt (eisenboek 12.36). Dezelfde vormen voor stand, notitie en beoordeling.
 *
 * **Wat per ronde verschilt, hoort er niet in.** De titel van de call, de sluitingsdatum en het budget
 * komen als verwijzing uit het contract van de call en niet als tekst, zodat een formulier uit het
 * sjabloon in een volgende ronde zonder handwerk klopt.
 */
export const FormTemplate = z.strictObject({
  id: TemplateId,
  naam: Text,
  /** Waarvoor het sjabloon is, in een paar woorden; optioneel. */
  beschrijving: z.string().trim().max(500).optional(),
  /** Het bouwblok waarvoor het is gemaakt; een formulier eruit kan een ander blok kiezen (D-41, D-69). */
  blok: Block,
  /** De huidige, goedgekeurde versie; zolang er geen is, die van het eerste voorstel. */
  versie: z.number().int().min(1),
  /** De sectietitels van die versie, voor de lijst; de volledige stand staat bij de versie. */
  sectietitels: z.array(TextDraft),
  vrijgegeven: z.boolean(),
  ingetrokken: z.boolean(),
  voorstel: z.number().int().min(1).optional(),
  gewijzigdDoor: Person,
  gewijzigdOp: Moment,
})
export type FormTemplate = z.infer<typeof FormTemplate>

/** Eén versie van een sjabloon: de volledige conceptstand, onveranderlijk, met notitie en beoordeling. */
export const FormTemplateVersion = z
  .strictObject({
    sjabloon: TemplateId,
    versie: z.number().int().min(1),
    definitie: DraftDefinitie,
    notitie: ChangeNote,
    /** Uit welk formulier deze versie is opgeslagen, als dat er een was. */
    bronDefinitie: DefinitieId.optional(),
    voorgesteldDoor: Person,
    voorgesteldOp: Moment,
    stand: VersionState,
    beoordeling: VersionReview.optional(),
  })
  .superRefine((version, ctx) => {
    if ((version.stand === 'voorstel') !== (version.beoordeling === undefined)) {
      ctx.addIssue({ code: 'custom', path: ['beoordeling'], message: 'een voorstel heeft geen beoordeling, een beoordeelde versie wel' })
    }
    if (version.beoordeling !== undefined && version.beoordeling.uitkomst !== version.stand) {
      ctx.addIssue({ code: 'custom', path: ['stand'], message: 'de stand volgt uit de uitkomst van de beoordeling' })
    }
  })
export type FormTemplateVersion = z.infer<typeof FormTemplateVersion>
